import { Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BudgetSummaryModel } from '../../../../core/models/data/budget-summary.model';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { BudgetSectionKind, BudgetUtilities } from '../../../../core/utilities/budget.utilities';

interface DonutData {
  label: string;
  sectionKind: BudgetSectionKind;
  budgeted: number;
  actual: number;
  variance: number;
  utilizationPercentage: number;
  visualPercentage: number;
  hasBudget: boolean;
  isOverBudget: boolean;
  trackColorVar: string;
  fillColorVar: string;
  varianceColorClass: string;
  variancePercentageDisplay: string;
}

@Component({
  selector: 'ms-budget-summary-chart',
  imports: [CommonModule, CurrencyDisplayPipe],
  templateUrl: './budget-summary-chart.component.html',
  styleUrl: './budget-summary-chart.component.scss',
})
export class BudgetSummaryChartComponent {

  public summary = input<BudgetSummaryModel | undefined>(undefined);
  public isLoading = input<boolean>(false);

  public readonly circumference = 2 * Math.PI * 45;

  public donuts = computed<DonutData[]>(() => {
    const summaryData = this.summary();
    if (!summaryData) return [];

    return [
      this.buildDonut('Revenue', 'revenue',
        summaryData.revenue.totalBudgeted,
        summaryData.revenue.totalActual,
        summaryData.revenue.totalVariance,
        'var(--bs-success-bg-subtle, #d1e7dd)',
        'var(--bs-success, #198754)'),

      this.buildDonut('Expenses', 'expenses',
        summaryData.expenses.totalBudgeted,
        summaryData.expenses.totalActual,
        summaryData.expenses.totalVariance,
        'var(--bs-danger-bg-subtle, #f8d7da)',
        'var(--bs-danger, #dc3545)'),

      this.buildNetDonut(summaryData),
    ];
  });

  public dashOffset(visualPercentage: number): number {
    return this.circumference - (visualPercentage / 100) * this.circumference;
  }

  private buildDonut(
    label: string,
    sectionKind: BudgetSectionKind,
    budgeted: number,
    actual: number,
    variance: number,
    trackColorVar: string,
    fillColorVar: string,
  ): DonutData {
    const utilization = BudgetUtilities.calculateUtilizationPercentage(actual, budgeted);
    const isOver = actual > budgeted && budgeted > 0;

    const expensesOver = sectionKind === 'expenses' && isOver;
    const fill = expensesOver ? 'var(--bs-danger, #dc3545)' : fillColorVar;

    return {
      label,
      sectionKind,
      budgeted,
      actual,
      variance,
      utilizationPercentage: utilization,
      visualPercentage: Math.min(utilization, 100),
      hasBudget: budgeted !== 0,
      isOverBudget: isOver,
      trackColorVar,
      fillColorVar: fill,
      varianceColorClass: BudgetUtilities.getVarianceColorClass(variance, sectionKind),
      variancePercentageDisplay: BudgetUtilities.formatVariancePercentage(variance, budgeted),
    };
  }

  private buildNetDonut(summaryData: BudgetSummaryModel): DonutData {
    const netBudgeted = summaryData.revenue.totalBudgeted - summaryData.expenses.totalBudgeted;
    const netActual = summaryData.revenue.totalActual - summaryData.expenses.totalActual;
    const netVariance = netActual - netBudgeted;

    return this.buildDonut(
      'Net Income',
      'revenue',
      netBudgeted,
      netActual,
      netVariance,
      'var(--bs-primary-bg-subtle, #cfe2ff)',
      'var(--bs-primary, #0d6efd)',
    );
  }
}
